<template>
  <div class="centralizador-root">

    <!-- ── Cabecera ─────────────────────────────────────────────────── -->
    <div class="cab-header px-4 pt-3 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap gap-2">
        <div class="d-flex align-center">
          <div class="header-icon-wrap mr-3">
            <v-icon color="primary" size="22">mdi-table-large</v-icon>
          </div>
          <div>
            <div class="cab-title">Acta Centralizador de Notas</div>
            <div class="cab-subtitle">Consulta el kardex centralizado por programa académico</div>
          </div>
        </div>

        <div class="d-flex align-center gap-2">
          <v-chip v-if="kardex" x-small color="success" label dark class="mr-1">
            <v-icon x-small left>mdi-check-circle</v-icon>
            Cargado
          </v-chip>
          <v-btn
            v-if="kardex"
            small
            color="error"
            dark
            depressed
            class="mr-1"
            @click="abrirDialogCentralizado">
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Centralizador
          </v-btn>
          <v-btn
            icon
            small
            :disabled="!programaId || cargando"
            title="Recargar"
            @click="buscarKardex">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Barra de búsqueda -->
      <div class="search-bar mt-3">
        <div class="search-bar-label mb-2">
          <v-icon x-small color="blue-grey" class="mr-1">mdi-information-outline</v-icon>
          Selecciona un programa para ver su acta centralizadora
        </div>
        <v-row dense align="center" no-gutters>
          <v-col cols="12" sm="8" md="8" lg="8">
            <v-autocomplete
              v-model="programaSeleccionado"
              :items="programas"
              :loading="buscandoProgramas"
              :search-input.sync="searchPrograma"
              item-text="nombreCompuesto"
              item-value="id"
              label="Buscar por nombre o código del programa..."
              dense
              outlined
              hide-details
              clearable
              no-filter
              return-object
              prepend-inner-icon="mdi-school-outline"
              class="search-input"
              :no-data-text="buscandoProgramas ? 'Buscando...' : 'Sin resultados'"
              @change="onProgramaSeleccionado">
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="autocomplete-title">
                    {{ item.nombreCompuesto }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="autocomplete-codigo">
                    <v-icon x-small color="blue-grey" class="mr-1">mdi-barcode</v-icon>
                    {{ item.codigo }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template #selection="{ item }">
                <span class="autocomplete-selected text-truncate">{{ item.nombreCompuesto }}</span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="auto" class="ml-2">
            <v-btn
              color="primary"
              depressed
              :loading="cargando"
              :disabled="!programaId"
              @click="buscarKardex"
              style="height: 40px;">
              <v-icon left>mdi-table-search</v-icon>
              Ver Acta
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- ── Loading ──────────────────────────────────────────────────── -->
    <div v-if="cargando" class="state-box">
      <v-progress-circular indeterminate color="primary" size="46" width="3" />
      <p class="state-text mt-3">Cargando acta centralizadora...</p>
      <p v-if="programaSeleccionado" class="state-subtext">{{ programaSeleccionado.nombreCompuesto }}</p>
    </div>

    <!-- ── Contenido del kardex ─────────────────────────────────────── -->
    <div v-else-if="kardex" class="px-4 pb-4 content-fade">

      <!-- Tarjeta de información del programa -->
      <v-card class="program-card mb-4" elevation="0">
        <div class="program-card-body">
          <div class="program-info-row">

            <!-- Info del programa -->
            <div class="program-info">
              <div class="d-flex align-center flex-wrap gap-2 mb-1">
                <v-chip x-small color="primary" label dark class="mr-1">
                  {{ kardex.programa.codigo }}
                </v-chip>
                <v-chip x-small color="blue-grey lighten-4" label>
                  V{{ kardex.programa.version }}
                </v-chip>
              </div>
              <div class="prog-postgrado">{{ kardex.postgrado.nombre }}</div>
              <div class="prog-nombre">{{ kardex.programa.nombre }}</div>
            </div>

            <div class="program-stats-divider"></div>

            <!-- Estadísticas -->
            <div class="program-stats">
              <div class="stats-row">
                <div class="stat-item">
                  <div class="stat-value primary--text">{{ kardex.modulos.length }}</div>
                  <div class="stat-label">
                    <v-icon x-small color="primary" class="mr-1">mdi-bookshelf</v-icon>
                    módulos
                  </div>
                </div>
                <div class="stat-item">
                  <div class="stat-value blue-grey--text text--darken-1">{{ kardex.estudiantes.length }}</div>
                  <div class="stat-label">
                    <v-icon x-small color="blue-grey" class="mr-1">mdi-account-group</v-icon>
                    estudiantes
                  </div>
                </div>
                <div class="stat-item">
                  <div class="stat-value success--text">{{ estudiantesAprobados }}</div>
                  <div class="stat-label">
                    <v-icon x-small color="success" class="mr-1">mdi-check-circle</v-icon>
                    aprobados
                  </div>
                </div>
                <div class="stat-item">
                  <div class="stat-value error--text">{{ estudiantesReprobados }}</div>
                  <div class="stat-label">
                    <v-icon x-small color="error" class="mr-1">mdi-close-circle</v-icon>
                    reprobados
                  </div>
                </div>
              </div>
              <div class="progress-row mt-2">
                <div class="progress-label">
                  <span class="progress-pct success--text">
                    {{ kardex.estudiantes.length ? Math.round(estudiantesAprobados / kardex.estudiantes.length * 100) : 0 }}%
                  </span>
                  <span class="progress-text">de aprobación</span>
                </div>
                <v-progress-linear
                  :value="kardex.estudiantes.length ? (estudiantesAprobados / kardex.estudiantes.length * 100) : 0"
                  color="success"
                  background-color="#fecdd3"
                  rounded
                  height="7"
                  class="mt-1" />
              </div>
            </div>

          </div>
        </div>
      </v-card>

      <!-- Tabla de Módulos (acordión) -->
      <v-expansion-panels v-model="modulosPanel" flat class="modulos-panel mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header class="modulos-panel-header">
            <div class="d-flex align-center">
              <v-icon small color="primary" class="mr-2">mdi-view-list</v-icon>
              <span class="section-label mb-0">Módulos del Programa</span>
              <v-chip x-small color="primary" dark class="ml-2">
                {{ kardex.modulos.length }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="modulos-panel-content">
            <div class="table-container">
              <table class="acta-table modulos-table">
                <thead>
                  <tr>
                    <th class="th-modulo">MÓDULO</th>
                    <th class="th-docente">DOCENTE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="modulo in kardex.modulos"
                    :key="modulo.id"
                    class="modulo-row">
                    <td class="td-modulo td-modulo--clickable" @click="abrirDialogModulo(modulo)">
                      <span class="modulo-num">{{ toRoman(modulo.numero) }}</span>
                      <span class="modulo-sep">:</span>
                      {{ modulo.nombre }}
                    </td>
                    <td class="td-docente">
                      <span v-if="modulo.docente" class="docente-info">
                        <span class="docente-nombre">
                          <v-icon x-small color="blue-grey" class="mr-1">mdi-account-tie</v-icon>
                          {{ modulo.docente }}
                        </span>
                        <small v-if="modulo.docenteCi" class="docente-ci">
                          CI: {{ modulo.docenteCi }}
                        </small>
                      </span>
                      <span v-else class="no-docente">No está asignado</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Tabla de Calificaciones -->
      <div class="d-flex align-center justify-space-between mb-2">
        <div class="section-label">
          <v-icon small color="primary" class="mr-1">mdi-table-account</v-icon>
          Calificaciones por Estudiante
        </div>
      </div>

      <div class="table-container grades-wrapper">
        <table class="acta-table grades-table">
          <thead>
            <tr>
              <th class="th-idx sticky-col">#</th>
              <th class="th-nombre sticky-col-2">APELLIDOS Y NOMBRES</th>
              <th
                v-for="modulo in kardex.modulos"
                :key="modulo.id"
                class="th-nota"
                :title="`${toRoman(modulo.numero)}: ${modulo.nombre}`">
                {{ toRoman(modulo.numero) }}
              </th>
              <th class="th-final">NOTA<br>FINAL</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(estudiante, index) in kardex.estudiantes"
              :key="estudiante.id"
              class="student-row">
              <td class="td-idx sticky-col">{{ index + 1 }}</td>
              <td class="td-nombre sticky-col-2">
                <span class="student-name">{{ estudiante.nombreCompleto }}</span>
                <small class="student-ci">CI: {{ estudiante.ci }}</small>
              </td>
              <td
                v-for="modulo in kardex.modulos"
                :key="modulo.id"
                class="td-nota">
                <span
                  :class="notaClass(estudiante.notas[modulo.id])"
                  class="nota-badge">
                  {{ formatNota(estudiante.notas[modulo.id]) }}
                </span>
              </td>
              <td class="td-final">
                <span
                  :class="notaFinalClass(calcularNotaFinal(estudiante.notas))"
                  class="nota-final-badge">
                  {{ calcularNotaFinal(estudiante.notas) }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="promedio-row">
              <td class="td-promedio-label sticky-col" colspan="2">
                <v-icon x-small color="blue-grey" class="mr-1">mdi-calculator-variant</v-icon>
                PROMEDIO
              </td>
              <td
                v-for="modulo in kardex.modulos"
                :key="modulo.id"
                class="td-promedio">
                <span class="promedio-badge">
                  {{ formatNota(calcularPromedioModulo(modulo.id)) }}
                </span>
              </td>
              <td class="td-promedio-final">
                <span class="promedio-final-badge">
                  {{ calcularPromedioGlobal() }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Leyenda -->
      <div class="grades-legend mt-2">
        <span class="legend-item">
          <span class="legend-dot dot-aprobado"></span>≥ {{ notaAprobacion }} Aprobado
        </span>
        <span class="legend-item">
          <span class="legend-dot dot-reprobado"></span>&lt; {{ notaAprobacion }} Reprobado
        </span>
        <span class="legend-item">
          <span class="legend-dot dot-cero"></span>0 Sin calificación
        </span>
        <span class="legend-item">
          <span class="legend-dot dot-vacia"></span>— No registrada
        </span>
      </div>
    </div>

    <!-- ── Estado vacío ─────────────────────────────────────────────── -->
    <div v-else class="state-box">
      <div class="empty-steps">
        <div class="empty-step">
          <div class="step-icon-wrap">
            <v-icon size="28" color="primary">mdi-school-outline</v-icon>
          </div>
          <div class="step-num">1</div>
          <div class="step-text">Busca el programa</div>
        </div>
        <v-icon size="18" color="grey lighten-2" class="step-arrow">mdi-chevron-right</v-icon>
        <div class="empty-step">
          <div class="step-icon-wrap step-icon-2">
            <v-icon size="28" color="primary lighten-1">mdi-cursor-default-click-outline</v-icon>
          </div>
          <div class="step-num step-num-2">2</div>
          <div class="step-text">Presiona Ver Acta</div>
        </div>
        <v-icon size="18" color="grey lighten-2" class="step-arrow">mdi-chevron-right</v-icon>
        <div class="empty-step">
          <div class="step-icon-wrap step-icon-3">
            <v-icon size="28" color="primary lighten-2">mdi-table-check</v-icon>
          </div>
          <div class="step-num step-num-3">3</div>
          <div class="step-text">Consulta las notas</div>
        </div>
      </div>
      <p class="state-text mt-4">Ingresa el nombre o código del programa y presiona <strong>Ver Acta</strong></p>
      <p class="state-subtext">Se mostrará el acta centralizadora con las notas de cada estudiante por módulo</p>
    </div>

    <dialog-calificacion-modulo ref="dialogModulo" />
    <dialog-calificacion-centralizado ref="dialogCentralizado" />
    <notifications group="notifications" position="top right" />
  </div>
</template>

<script>
import CalificacionCentralizadoService from '../../services/calificacion-centralizado.service';
import ProgramasService from '../../services/programas.service';
import DialogCalificacionModulo from '../components/DialogCalificacionModulo.vue';
import DialogCalificacionCentralizado from '../components/DialogCalificacionCentralizado.vue';

const ROMANOS = [
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
];

export default {
  name: 'CalificacionCentralizadorLayout',
  components: { DialogCalificacionModulo, DialogCalificacionCentralizado },
  data() {
    return {
      programaId: null,
      programaSeleccionado: null,
      programas: [],
      buscandoProgramas: false,
      searchPrograma: null,
      retardo: null,
      kardex: null,
      cargando: false,
      modulosPanel: -1,
      notaAprobacion: 71,
    };
  },
  watch: {
    searchPrograma(val) {
      if (!val || val.length < 2) return;
      clearTimeout(this.retardo);
      this.retardo = setTimeout(() => this.fetchProgramas(val), 400);
    },
  },
  computed: {
    estudiantesAprobados() {
      if (!this.kardex) return 0;
      return this.kardex.estudiantes.filter(
        (e) => this.calcularNotaFinal(e.notas) >= this.notaAprobacion,
      ).length;
    },
    estudiantesReprobados() {
      if (!this.kardex) return 0;
      return this.kardex.estudiantes.filter(
        (e) => this.calcularNotaFinal(e.notas) < this.notaAprobacion && this.calcularNotaFinal(e.notas) > 0,
      ).length;
    },
  },
  methods: {
    abrirDialogModulo(modulo) {
      this.$refs.dialogModulo.abrir(modulo);
    },
    abrirDialogCentralizado() {
      const nombre = this.kardex?.programa?.nombre || `Programa ${this.programaId}`;
      const programaUniversidadId = this.kardex?.programa?.programaUniversidadId || null;
      this.$refs.dialogCentralizado.abrir(this.programaId, nombre, programaUniversidadId);
    },
    onProgramaSeleccionado(programa) {
      if (!programa) {
        this.programaId = null;
        this.kardex = null;
        return;
      }
      this.programaId = programa.id;
      this.buscarKardex();
    },
    async fetchProgramas(q) {
      this.buscandoProgramas = true;
      try {
        const { data } = await ProgramasService.buscarProgramas({ q });
        this.programas = data?.datos || [];

      } catch {
        this.programas = [];
      } finally {
        this.buscandoProgramas = false;
      }
    },
    toRoman(num) {
      return ROMANOS[num - 1] || String(num);
    },
    formatNota(nota) {
      if (nota === null || nota === undefined) return '—';
      return nota;
    },
    calcularNotaFinal(notas) {
      const valores = Object.values(notas).filter((v) => v !== null && v !== undefined);
      if (valores.length === 0) return 0;
      const suma = valores.reduce((acc, v) => acc + v, 0);
      return Math.round(suma / valores.length);
    },
    calcularPromedioModulo(moduloId) {
      if (!this.kardex) return null;
      const valores = this.kardex.estudiantes
        .map((e) => e.notas[moduloId])
        .filter((v) => v !== null && v !== undefined && v > 0);
      if (valores.length === 0) return null;
      return Math.round(valores.reduce((acc, v) => acc + v, 0) / valores.length);
    },
    calcularPromedioGlobal() {
      if (!this.kardex) return 0;
      const finales = this.kardex.estudiantes
        .map((e) => this.calcularNotaFinal(e.notas))
        .filter((v) => v > 0);
      if (finales.length === 0) return 0;
      return Math.round(finales.reduce((acc, v) => acc + v, 0) / finales.length);
    },
    notaClass(nota) {
      if (nota === null || nota === undefined) return 'nota--vacia';
      if (nota === 0) return 'nota--cero';
      if (nota >= this.notaAprobacion) return 'nota--aprobado';
      return 'nota--reprobado';
    },
    notaFinalClass(notaFinal) {
      if (notaFinal === 0) return 'final--cero';
      if (notaFinal >= this.notaAprobacion) return 'final--aprobado';
      return 'final--reprobado';
    },
    async buscarKardex() {
      if (!this.programaId) return;

      this.cargando = true;
      this.kardex = null;

      try {
        const { data } = await CalificacionCentralizadoService.getProgramaKardex({
          formato: 'vista',
          programaId: this.programaId,
        });
        this.kardex = data?.datos || null;
        this.notaAprobacion = this.kardex?.programa?.notaAprobacion || 71;
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al obtener el kardex';
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: mensaje,
          type: 'error',
        });
      } finally {
        this.cargando = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Contenedor raíz ────────────────────────────────────────────── */
.centralizador-root {
  border: 1px solid #dce6f0;
  border-radius: 10px;
  margin: 0 8px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(29, 78, 216, 0.06);
}

/* ── Cabecera ───────────────────────────────────────────────────── */
.cab-header {
  background: linear-gradient(135deg, #eef3ff 0%, #f8fafc 100%);
  border-bottom: 1px solid #dce6f0;
}

.header-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cab-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a2640;
  line-height: 1.3;
}

.cab-subtitle {
  font-size: 12px;
  color: #6b7a8d;
  margin-top: 1px;
}

.search-bar {
  background: #f6f8fb;
  border: 1px solid #dce6f0;
  border-radius: 8px;
  padding: 10px 14px;
}

.search-bar-label {
  font-size: 11px;
  color: #6b7a8d;
  display: flex;
  align-items: center;
}

::v-deep .search-input .v-input__slot {
  min-height: 40px !important;
}

/* ── Estados (loading / vacío) ──────────────────────────────────── */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  padding: 40px 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 60%);
}

.state-text {
  font-size: 14px;
  color: #4b5563;
  margin: 0;
  text-align: center;
}

.state-subtext {
  font-size: 12px;
  color: #9ca3af;
  margin: 4px 0 0;
  text-align: center;
}

/* ── Estado vacío: pasos ────────────────────────────────────────── */
.empty-steps {
  display: flex;
  align-items: center;
  gap: 8px;
}

.empty-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.step-icon-wrap {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #eff6ff;
  border: 2px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-icon-2 {
  background: #f0f9ff;
  border-color: #bae6fd;
}

.step-icon-3 {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.step-num {
  font-size: 10px;
  font-weight: 700;
  color: #1d4ed8;
  background: #dbeafe;
  border-radius: 10px;
  padding: 1px 7px;
}

.step-num-2 {
  color: #0369a1;
  background: #e0f2fe;
}

.step-num-3 {
  color: #15803d;
  background: #dcfce7;
}

.step-text {
  font-size: 11px;
  color: #6b7280;
  text-align: center;
  max-width: 68px;
}

.step-arrow {
  margin-bottom: 18px;
}

/* ── Contenido con fade ─────────────────────────────────────────── */
.content-fade {
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Tarjeta de información del programa ────────────────────────── */
.program-card {
  border: 1px solid #dce6f0 !important;
  border-radius: 10px !important;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%) !important;
}

.program-card-body {
  padding: 14px 16px;
}

.program-info-row {
  display: flex;
  align-items: stretch;
  gap: 16px;
  flex-wrap: wrap;
}

.program-info {
  flex: 1;
  min-width: 200px;
}

.program-stats-divider {
  width: 1px;
  background: #dce6f0;
  align-self: stretch;
  flex-shrink: 0;
}

.program-stats {
  flex: 1;
  min-width: 200px;
}

.stats-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 52px;
}

.stat-value {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
}

.stat-label {
  font-size: 10px;
  color: #6b7a8d;
  display: flex;
  align-items: center;
  margin-top: 1px;
}

.progress-row {
  max-width: 300px;
}

.progress-label {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.progress-pct {
  font-size: 14px;
  font-weight: 700;
}

.progress-text {
  font-size: 11px;
  color: #6b7a8d;
}

.prog-postgrado {
  font-size: 13px;
  font-weight: 700;
  color: #1d4ed8;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  line-height: 1.4;
}

.prog-nombre {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-top: 2px;
  line-height: 1.4;
}

.gap-2 {
  gap: 8px;
}

/* ── Etiqueta de sección ────────────────────────────────────────── */
.section-label {
  font-size: 12px;
  font-weight: 600;
  color: #1d4ed8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

/* ── Tablas genéricas ───────────────────────────────────────────── */
.table-container {
  border: 1px solid #dce6f0;
  border-radius: 8px;
  overflow: hidden;
}

.grades-wrapper {
  overflow-x: auto;
}

.acta-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.acta-table thead tr {
  background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
}

.acta-table thead tr th {
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.4px;
  padding: 9px 12px;
  border-right: 1px solid rgba(255, 255, 255, 0.15);
  white-space: nowrap;
  text-align: center;
}

.acta-table thead tr th:last-child {
  border-right: none;
}

.acta-table tbody tr {
  transition: background 0.15s ease;
}

.acta-table tbody tr:nth-child(odd) {
  background: #fff;
}

.acta-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.acta-table tbody tr:hover {
  background: #eff6ff !important;
}

.acta-table tbody tr td {
  border-bottom: 1px solid #e9eef4;
  border-right: 1px solid #e9eef4;
  padding: 5px 8px;
  vertical-align: middle;
}

.acta-table tbody tr td:last-child {
  border-right: none;
}

/* ── Tabla Módulos ──────────────────────────────────────────────── */
.th-modulo {
  width: 65%;
  text-align: left !important;
}

.th-docente {
  width: 35%;
  text-align: left !important;
}

.td-modulo {
  text-align: left;
  color: #1e293b;
}

.td-modulo--clickable {
  cursor: pointer;
}

.td-modulo--clickable:hover {
  background: #eff6ff !important;
  color: #1d4ed8;
}

.modulo-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 20px;
  background: #1d4ed8;
  color: #fff;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  margin-right: 6px;
}

.modulo-sep {
  color: #9ca3af;
  margin-right: 4px;
}

.td-docente {
  text-align: left;
}

.docente-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.docente-nombre {
  display: inline-flex;
  align-items: center;
  color: #374151;
  font-size: 12px;
  font-weight: 500;
}

.docente-ci {
  font-size: 10px;
  color: #6b7280;
  padding-left: 18px;
}

.no-docente {
  color: #9ca3af;
}

/* ── Tabla de calificaciones ────────────────────────────────────── */
.th-idx {
  width: 32px;
  min-width: 32px;
}

.th-nombre {
  min-width: 180px;
  text-align: left !important;
}

.th-nota {
  width: 34px;
  min-width: 34px;
  font-size: 11px;
  line-height: 1.2;
  cursor: default;
}

.th-final {
  width: 60px;
  min-width: 60px;
  background: rgba(255, 255, 255, 0.15) !important;
  line-height: 1.3;
  border-left: 2px solid rgba(255, 255, 255, 0.4) !important;
}

/* Columnas pegajosas */
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: inherit;
}

.sticky-col-2 {
  position: sticky;
  left: 32px;
  z-index: 2;
  background: inherit;
  border-right: 2px solid #dce6f0 !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.04);
}

.acta-table thead .sticky-col,
.acta-table thead .sticky-col-2 {
  z-index: 3;
  background: #1d4ed8;
}

.td-idx {
  text-align: center;
  color: #6b7a8d;
  font-weight: 600;
  font-size: 11px;
}

.td-nombre {
  text-align: left;
  padding: 5px 10px !important;
}

.student-name {
  display: block;
  font-weight: 600;
  color: #1e293b;
  font-size: 12px;
  line-height: 1.3;
}

.student-ci {
  display: block;
  font-size: 10px;
  color: #9ca3af;
}

.td-nota {
  text-align: center;
  padding: 4px 2px !important;
}

.td-final {
  text-align: center;
  border-left: 2px solid #dce6f0 !important;
}

/* ── Badges de notas ────────────────────────────────────────────── */
.nota-badge {
  display: inline-block;
  min-width: 26px;
  font-size: 11px;
  text-align: center;
  padding: 2px 4px;
  border-radius: 4px;
}

.nota--vacia {
  color: #d1d5db;
}

.nota--cero {
  color: #6b7280;
  background: #f3f4f6;
}

.nota--aprobado {
  color: #166534;
  background: #dcfce7;
  font-weight: 600;
}

.nota--reprobado {
  color: #9f1239;
  background: #fff1f2;
  font-weight: 600;
}

.nota-final-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 24px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}

.final--cero {
  color: #6b7280;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
}

.final--aprobado {
  color: #15803d;
  background: #dcfce7;
  border: 1px solid #86efac;
}

.final--reprobado {
  color: #b91c1c;
  background: #fee2e2;
  border: 1px solid #fca5a5;
}

/* ── Fila de promedios (tfoot) ──────────────────────────────────── */
.promedio-row {
  background: linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%) !important;
  border-top: 2px solid #dce6f0;
}

.promedio-row td {
  border-bottom: none !important;
  padding: 6px 8px !important;
}

.td-promedio-label {
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  color: #4b5e7a;
  letter-spacing: 0.4px;
  padding: 6px 10px !important;
  white-space: nowrap;
  border-right: 2px solid #dce6f0 !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.04);
  background: #f0f4ff;
}

.td-promedio {
  text-align: center;
  border-right: 1px solid #e9eef4 !important;
}

.td-promedio-final {
  text-align: center;
  border-left: 2px solid #dce6f0 !important;
}

.promedio-badge {
  display: inline-block;
  min-width: 26px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  padding: 2px 4px;
  border-radius: 4px;
  color: #374151;
  background: #e0e7ff;
}

.promedio-final-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 22px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #1d4ed8;
  background: #dbeafe;
  border: 1px solid #bfdbfe;
}

/* ── Leyenda de colores ─────────────────────────────────────────── */
.grades-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding: 4px 2px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #6b7280;
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.dot-aprobado  { background: #dcfce7; border: 1px solid #86efac; }
.dot-reprobado { background: #fff1f2; border: 1px solid #fca5a5; }
.dot-cero      { background: #f3f4f6; border: 1px solid #e5e7eb; }
.dot-vacia     { background: #fff;    border: 1px solid #d1d5db; }

/* ── Acordión de módulos ────────────────────────────────────────── */
.modulos-panel {
  border: 1px solid #dce6f0;
  border-radius: 8px !important;
  overflow: hidden;
}

::v-deep .modulos-panel .v-expansion-panel {
  border-radius: 8px !important;
}

::v-deep .modulos-panel .v-expansion-panel::before {
  box-shadow: none;
}

.modulos-panel-header {
  background: linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%) !important;
  border-radius: 8px !important;
  min-height: 44px !important;
  padding: 0 16px !important;
}

::v-deep .modulos-panel .v-expansion-panel-header__icon .v-icon {
  color: #1d4ed8 !important;
}

::v-deep .modulos-panel .v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.modulos-panel-content .table-container {
  border-radius: 0;
  border: none;
  border-top: 1px solid #dce6f0;
}

/* ── Autocomplete de programas ──────────────────────────────────── */
.autocomplete-title {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  white-space: normal;
  line-height: 1.4;
}

.autocomplete-codigo {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.autocomplete-selected {
  font-size: 13px;
  color: #1e293b;
  max-width: 100%;
  display: block;
}
</style>
